<template>
  <div v-if="hasWordAlignment">
    <span
      v-for="piece in textPieces"
      class="piece"
      :key="piece.id"
      :class="getSpanClass(piece.startTime, piece.endTime, piece.wordId)"
      @click="clickPiece(piece)"
      >{{ piece.text }}</span
    >
  </div>
  <div v-else>
    {{ text }}
  </div>
</template>

<script>
export default {
  props: [
    "text",
    "wordAlignment",
    "currentTime",
    "textSelectionMode",
    "dictationMode",
    "obscurityLevel"
  ],
  data() {
    return {
      textStartSelected: false,
      textStartTime: 0,
      textStartIndex: 0,
      textEndSelected: false,
      textEndTime: 1000,
      textEndIndex: 1000,
      currentHiddenWordId: -1,
      correctWordIds: [],
      wrongWordIds: []
    };
  },
  computed: {
    hasWordAlignment() {
      return !!this.wordAlignment;
    },
    currentHiddenWord() {
      return this.currentHiddenWordId >= 0 && this.currentHiddenWordId < 9999
        ? this.textPieces[2 * this.currentHiddenWordId].text
        : "";
    },
    hiddenWordIndices() {
      if (!this.dictationMode) return []; // short-circuit if not in dictation mode
      const alignedWords = this.wordAlignment
        ? this.wordAlignment.words.map((w, i) => ({ word: w.text, index: i }))
        : [];
      alignedWords.sort((a, b) => b.word.length - a.word.length);
      const hiddenCount = (this.obscurityLevel * alignedWords.length) / 100;
      const hiddenWords = alignedWords.slice(0, hiddenCount);
      return hiddenWords.map(w => w.index).sort((a, b) => a - b);
    },
    textPieces() {
      const alignedWords = this.wordAlignment ? this.wordAlignment.words : [];
      let pieces = [];
      for (let i = 0; i < alignedWords.length; i++) {
        const word = alignedWords[i];
        let piece = {
          id: 2 * i,
          wordId: i,
          text: this.text.substring(word.startIndex, word.endIndex + 1),
          startTime: word.startTime,
          endTime: word.endTime,
          startIndex: word.startIndex,
          endIndex: word.endIndex
        };
        pieces.push(piece);
        // if there is another word following
        if (i < alignedWords.length - 1) {
          const nextWord = alignedWords[i + 1];
          let midPiece = {
            id: 2 * i + 1,
            text: this.text.substring(word.endIndex + 1, nextWord.startIndex),
            startTime: word.endTime,
            endTime: nextWord.startTime,
            startIndex: word.endIndex + 1,
            endIndex: nextWord.startIndex - 1
          };
          pieces.push(midPiece);
        }
        // if last word but still some characters left
        if (
          i == alignedWords.length - 1 &&
          word.endIndex < this.text.length - 1
        ) {
          let lastPiece = {
            id: 2 * i + 1,
            text: this.text.substring(word.endIndex + 1),
            startTime: word.endTime,
            endTime: word.endTime,
            startIndex: word.endIndex + 1,
            endIndex: this.text.length - 1
          };
          pieces.push(lastPiece);
        }
      }
      return pieces;
    }
  },
  methods: {
    clickPiece(piece) {
      if (this.textSelectionMode) {
        if (
          !this.textStartSelected ||
          piece.startIndex < this.textStartIndex ||
          this.textEndSelected
        ) {
          this.setAsTextStart(piece);
          this.setAsTextEnd(piece);
          this.textStartSelected = true;
          this.textEndSelected = false;
          this.emitSelectedText();
        } else {
          this.setAsTextEnd(piece);
          this.textEndSelected = true;
          this.emitSelectedText();
        }
      } else {
        this.$emit("selectedTime", piece.startTime);
      }
    },
    setAsTextStart(piece) {
      this.textStartTime = piece.startTime;
      this.textStartIndex = piece.startIndex;
    },
    setAsTextEnd(piece) {
      this.textEndTime = piece.endTime;
      this.textEndIndex = piece.endIndex;
    },
    emitSelectedText() {
      const selectedText = this.text.substring(
        this.textStartIndex,
        this.textEndIndex + 1
      );
      this.$emit(
        "selectedText",
        selectedText,
        this.textStartTime,
        this.textEndTime
      );
    },
    getSpanClass(startTime, endTime, wordId) {
      return {
        passed: this.currentTime >= endTime,
        current: this.currentTime >= startTime && this.currentTime < endTime,
        greyed:
          this.textStartTime >= endTime ||
          this.textEndTime <= startTime ||
          (this.textSelectionMode &&
            !this.textStartSelected &&
            !this.textEndSelected),
        hidden: this.hiddenWordIndices.includes(wordId),
        correct: this.correctWordIds.includes(wordId),
        wrong: this.wrongWordIds.includes(wordId),
        currentHidden: this.currentHiddenWordId === wordId
      };
    },
    moveToNextHiddenWord() {
      const nextWordId = this.hiddenWordIndices.find(
        x => x > this.currentHiddenWordId
      );
      if (nextWordId) {
        this.currentHiddenWordId = nextWordId;
      } else {
        this.currentHiddenWordId = 9999;
      }
      this.emitCurrentHiddenWord();
    },
    resetCurrentHiddenWord() {
      this.currentHiddenWordId =
        this.hiddenWordIndices.length > 0 ? this.hiddenWordIndices[0] : -1;
      this.correctWordIds = [];
      this.wrongWordIds = [];
      this.emitCurrentHiddenWord();
    },
    emitCurrentHiddenWord() {
      this.$emit(
        "currentHiddenWord",
        this.currentHiddenWord,
        this.currentHiddenWordId
      );
    },
    handleCheckResult(wordId, correct) {
      if (correct) {
        this.correctWordIds.push(wordId);
      } else {
        this.wrongWordIds.push(wordId);
      }
      this.moveToNextHiddenWord();
    }
  },
  watch: {
    obscurityLevel() {
      this.resetCurrentHiddenWord();
    },
    dictationMode() {
      this.resetCurrentHiddenWord();
    }
  },
  mounted() {
    this.resetCurrentHiddenWord();
    this.$parent.$on("hiddenWordCheckResult", this.handleCheckResult);
  }
};
</script>

<style lang="scss" scoped>
.piece {
  &.current {
    border-bottom: 4px solid;
  }

  &.passed {
    border-bottom: 2px solid;
  }

  &.greyed {
    color: lightgray;
  }

  &.hidden {
    background-color: cadetblue;
    color: cadetblue;
    user-select: none;
    border-color: #2e2e2e;

    &.correct {
      background-color: transparent;
      box-shadow: inset 0px 0px 0px 1px lightgreen;
    }

    &.wrong {
      background-color: transparent;
      box-shadow: inset 0px 0px 0px 1px palevioletred;
    }

    &.currentHidden {
      border: 3px solid darkblue;
    }
  }

  &:hover {
    border-bottom: 2px dotted #2e2e2e;
  }

  cursor: pointer;
  line-height: 2.1rem;
}
</style>
